import { Component, Inject, OnInit, AfterViewInit } from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialogRef,
  MatDialogModule
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { FormArray, FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { S3File } from '../../model/S3_file_dto';
import { SpinnerVisibilityService } from 'ng-http-loader';
import { WorkbiesService } from '../../services/workbies.service';
import { MatSelectModule } from '@angular/material/select';
import { CommonModule } from '@angular/common';
import { StatusDTO } from '../../model/status-dto';
import { FileUser } from '../../model/cammeler-dto';
import { STATUS_CONSTANTS } from '../../utils/constants';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { FileUserRequestDTO } from '../../model/file-user-request-dto';
import { ObsProfileRequestDTO } from '../../model/obs-profile-request-dto';

@Component({
  selector: 'app-admin-modal-files',
  standalone: true,
  imports: [
    MatFormFieldModule,
    MatInputModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatDialogModule,
    MatSelectModule,
    CommonModule,
    MatCheckboxModule
  ],
  templateUrl: './admin-modal-files.component.html',
  styleUrl: './admin-modal-files.component.scss'
})
export class AdminModalFilesComponent implements OnInit {

  form: FormGroup;
  dataStatus: StatusDTO[];

  ngOnInit(): void {
    this.initForm();
    this.formValueChanges();
    setTimeout(() => {
      this.form.updateValueAndValidity();
    }, 0);
  }
  

  constructor(
    private fb: FormBuilder,
    private service: WorkbiesService,
    private spinner: SpinnerVisibilityService,
    @Inject(MAT_DIALOG_DATA) public data: any,
    public dialogRef: MatDialogRef<AdminModalFilesComponent>
  ) {
    this.getStatusFiles();
  }

  get filesArray() {
    return this.form.get('filesArray') as FormArray;
  }

  formValueChanges() {
    this.form.get('isCorrectDocument')?.valueChanges.subscribe((value: any) => {
      const obsDocument = this.form.get('obsDocument');
      if (value) {
        obsDocument?.clearValidators();
      } else {
        obsDocument?.setValidators(Validators.required);
      }

      obsDocument?.updateValueAndValidity();
    });

    this.form.get('isCorrectIssueDate')?.valueChanges.subscribe((value: any) => {
      const obsIssueDate = this.form.get('obsIssueDate');
      if (value) {
        obsIssueDate?.clearValidators();
      } else {
        obsIssueDate?.setValidators(Validators.required);
      }

      obsIssueDate?.updateValueAndValidity();
    });

    this.form.get('isCorrectDescription')?.valueChanges.subscribe((value: any) => {
      const obsDescription = this.form.get('obsDescription');
      if (value) {
        obsDescription?.clearValidators();
      } else {
        obsDescription?.setValidators(Validators.required);
      }

      obsDescription?.updateValueAndValidity();
    });

    this.form.get('isCorrectDocumentType')?.valueChanges.subscribe((value: any) => {
      const obsDocumentType = this.form.get('obsDocumentType');
      if (value) {
        obsDocumentType?.clearValidators();
      } else {
        obsDocumentType?.setValidators(Validators.required);
      }

      obsDocumentType?.updateValueAndValidity();
    });
    this.form.updateValueAndValidity();
  }

  initForm() {
    this.form = this.fb.group({
      isCorrectDocument: new FormControl(false),
      obsDocument: new FormControl('', Validators.required),
      isCorrectIssueDate: new FormControl(false),
      obsIssueDate: new FormControl('', Validators.required),
      isCorrectDescription: new FormControl(false),
      obsDescription: new FormControl('', Validators.required),
      isCorrectDocumentType: new FormControl(false),
      obsDocumentType: new FormControl('', Validators.required),
      filesArray: this.fb.array([], { updateOn: 'change' })
    });
    
    this.data.files.forEach((file: FileUser) => {
      const fileGroup = this.fb.group({
        idFile: new FormControl(file.idFile),
        uuidFile: new FormControl(file.uuidFile),
        fileType: new FormControl(file.fileType),
        statusFile: new FormControl('', Validators.required),
        observation: new FormControl('')
      });
      fileGroup.get('statusFile')?.valueChanges.subscribe((value: any) => {
        const observationControl = fileGroup.get('observation');
        
        if (value.code === STATUS_CONSTANTS.FILE_REJECT_STATUS_CODE) {
          observationControl?.setValidators(Validators.required);
        } else {
          observationControl?.clearValidators();
        }
        
        observationControl?.updateValueAndValidity();
        this.form.updateValueAndValidity();
      });
    
      this.filesArray.push(fileGroup);
    });    
  }

  getStatusFiles() {
    this.spinnerShow();
    this.service.getStatusFiles().subscribe({
      next: (resp: any) => {
        this.dataStatus = resp.data.filter((item: StatusDTO) => { return item.code !== STATUS_CONSTANTS.FILE_TO_REVIEW_STATUS_CODE });
        this.spinnerHide();
      },
      error: (err: any) => {
        console.log(err);
        this.spinnerHide();
      }
    })
  }

  openDocument(fileId: string, event: Event) {
    event.stopPropagation();
    event.preventDefault();
    const body: S3File = {
      fileId,
      idUser: this.data.data.id
    };
    this.spinnerShow();
    this.service.getS3File(body).subscribe({
      next: (response: any) => {
        const preSignedUrl = response.data;
        window.open(preSignedUrl, '_blank');
        this.spinnerHide();
      },
      error: (err: any) => {
        console.log(err);
        this.spinnerHide();
      }
    })
  }

  getFileType(fileName: string): string {
    const extension = fileName.split('.').pop()?.toLowerCase();
    switch (extension) {
      case 'pdf': return 'application/pdf';
      case 'jpg': case 'jpeg': return 'image/jpeg';
      case 'png': return 'image/png';
      case 'txt': return 'text/plain';
      case 'doc': return 'application/msword';
      case 'docx': return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
      default: return 'application/octet-stream';
    }
  }

  spinnerShow() {
    this.spinner.show();
  }

  spinnerHide() {
    this.spinner.hide();
  }

  closeDialog() {
    this.dialogRef.close();
  }

  saveData() {
    const files: FileUserRequestDTO[] = this.filesArray.controls.map((item) => {
      const file: FileUserRequestDTO = {
        idFile: item.get('idFile')?.value,
        idStatus: item.get('statusFile')?.value.idStatus,
        obs: item.get('observation')?.value
      };
      return file;
    });

    const obsProfile: ObsProfileRequestDTO = {
      idUser: this.data.data.id,
      files: files,
      isCorrectDescription: this.form.get('isCorrectDescription')?.value,
      isCorrectDocumentNumber: this.form.get('isCorrectDocument')?.value,
      isCorrectIssueDate: this.form.get('isCorrectIssueDate')?.value,
      isCorrectDocumentType: this.form.get('isCorrectDocumentType')?.value,
      obsDescription: this.form.get('obsDescription')?.value,
      obsDocumentNumber: this.form.get('obsDocument')?.value,
      obsIssueDate: this.form.get('obsIssueDate')?.value,
      obsDocumentType: this.form.get('obsDocumentType')?.value
    };
    this.spinner.show();
    this.service.saveObsData(obsProfile).subscribe({
      next: (response: any) => {
        console.log(response);
        this.spinner.hide();
        this.dialogRef.close(true);
      },
      error: (err: any) => {
        console.log(err);
        this.spinner.hide();
        this.dialogRef.close();
      }
    });
  }

  rejectedStatus() {
    return STATUS_CONSTANTS.FILE_REJECT_STATUS_CODE;
  }

}
